import { Flex, Spinner } from "@chakra-ui/react";
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { InfoIcon, MoreHorizontal, Pin, PinOff } from "lucide-react";
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { Button } from "../../ui/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../ui/components/ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/components/ui/table";
// import GlobalFilter from "./GlobalFilter";
import { OrganizationListResponseDto } from "@/lib/interfaces/organizations";
import { useAngeheftete } from "./useAngeheftete";
import { Paginated } from "@/lib/interfaces/utils";
import useApiRequest from "../../lib/hooks/useRequest";
import SearchInput from "../../ui/components/ui/search-input";

const columnNames: { [key: string]: string } = {
  name: "Unternehmen",
  ceo: "CEO",
};

/**
 * Die Abo-Staende von Stripe, auf Deutsch und eingefaerbt. Gesucht wird in Stripe ueber die
 * Abo-Id, nicht ueber das Statuswort — die englische Schreibweise bringt hier nichts und
 * steht neben einer sonst deutschen Oberflaeche nur schief.
 */
const STATUS: Record<string, { label: string; farbe: string }> = {
  active: { label: "Aktiv", farbe: "bg-green-50 text-green-800" },
  trialing: { label: "Testphase", farbe: "bg-sky-50 text-sky-800" },
  past_due: { label: "Zahlung offen", farbe: "bg-amber-50 text-amber-800" },
  unpaid: { label: "Unbezahlt", farbe: "bg-amber-50 text-amber-800" },
  paused: { label: "Pausiert", farbe: "bg-amber-50 text-amber-800" },
  canceled: { label: "Gekündigt", farbe: "bg-gray-100 text-gray-700" },
  incomplete: { label: "Unvollständig", farbe: "bg-gray-100 text-gray-700" },
  incomplete_expired: { label: "Abgelaufen", farbe: "bg-gray-100 text-gray-700" },
};

/** Ohne Abo gibt es keinen Stripe-Stand — ein eigener Zustand, in derselben Form. */
const OHNE_ABO = { label: "Kein Abo", farbe: "bg-rose-50 text-rose-800" };

/** Kein Wert heisst nicht Fehler. Ein Strich sagt das, ohne die Zeile zu alarmieren. */
const OhneWert = () => <span className="text-gray-400">—</span>;

export const DataTable = ({ setSelectedOrg }: { setSelectedOrg: Dispatch<SetStateAction<string | undefined>> }) => {
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const { apiRequest } = useApiRequest();
  const { angeheftete, istAngeheftet, umschalten, grenzeErreicht } = useAngeheftete();
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  // Reset pagination when search text changes
  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [searchText]);

  const login = async (id: string) => {
    await apiRequest(`organizations/${id}/login`, "POST");
    window.location.reload();
  };

  /**
   * Dasselbe Menue in der Liste und im Block der Angehefteten (KAN-298).
   *
   * Einloggen ist der haeufigste Griff und bleibt die Zeile selbst (KAN-276, A9). Alles
   * Weitere liegt hier: Details und Anheften. Zwei verschiedene Menues an zwei Stellen
   * waeren zwei Orte zum Suchen.
   */
  const aktionenMenue = (orga: OrganizationListResponseDto): JSX.Element => {
    const angeheftet = istAngeheftet(orga._id);

    return (
      <div className="flex items-center justify-end">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="secondary"
              size="icon"
              className="h-8 w-8"
              aria-label={`Weitere Aktionen fuer ${orga.name}`}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              className="flex gap-2 cursor-pointer"
              // Das Menue haengt zwar woanders in der Seite, im React-Baum bleibt es ein
              // Kind der Zeile. Ohne das hier loggte ein Klick auf "Details" mit ein.
              onClick={(e) => {
                e.stopPropagation();
                setSelectedOrg(orga._id);
              }}
            >
              <InfoIcon className="h-4 w-4 text-primary self-center" />
              Details
            </DropdownMenuItem>
            <DropdownMenuItem
              className="flex gap-2 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                umschalten(orga, !angeheftet);
              }}
            >
              {angeheftet ? (
                <PinOff className="h-4 w-4 text-primary self-center" />
              ) : (
                <Pin className="h-4 w-4 text-primary self-center" />
              )}
              {angeheftet ? "Nicht mehr anheften" : "Anheften"}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  };

  const columns: ColumnDef<OrganizationListResponseDto>[] = [
    {
      accessorKey: "name",
      header: () => columnNames.name,
      cell: ({ row }) => (
        // Die ganze Zeile fuehrt hinein; der Name ist zusaetzlich ein echter Knopf, sonst
        // kaeme man mit der Tastatur nicht hin.
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            login(row.original._id);
          }}
          className="text-left text-sm font-medium text-gray-900"
        >
          {row.getValue("name")}
        </button>
      ),
    },
    {
      accessorKey: "ceo",
      header: () => columnNames.ceo,
      cell: ({ row }) => <span className="text-sm text-gray-700">{row.getValue("ceo")}</span>,
    },
    {
      accessorKey: "subscriptionStatus",
      header: "Status",
      cell: ({ row }) => {
        const value = row.getValue<string | undefined>("subscriptionStatus");
        // Einen unbekannten Stand zeigt Stripe im Original — raten waere schlimmer als
        // ein fremdes Wort, das sich nachschlagen laesst.
        const stand = value ? (STATUS[value] ?? { label: value, farbe: "bg-gray-100 text-gray-700" }) : OHNE_ABO;

        return (
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${stand.farbe}`}>{stand.label}</span>
        );
      },
    },
    {
      accessorKey: "subscriptionId",
      header: "Abo-Id",
      cell: ({ row }) => {
        const value = row.getValue<string | undefined>("subscriptionId");
        if (!value) return <OhneWert />;

        // Die Stripe-Kennung ist lang und wird nur zum Nachschlagen gebraucht. Sie steht
        // deshalb leise da und darf die Spalte nicht auseinanderziehen.
        return (
          <span className="block max-w-[260px] truncate font-mono text-[13px] text-gray-500" title={value}>
            {value}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: "Aktionen",
      enableHiding: false,
      cell: ({ row }) => aktionenMenue(row.original),
    },
  ];

  const [data, setData] = useState<OrganizationListResponseDto[]>([]);
  const [pageCount, setPageCount] = useState(0);
  const [, setItemCount] = useState(0);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  // fetch the data
  // as soon as it is fetched set the data to the table

  const table = useReactTable({
    data,
    columns,
    // pageCount: Math.ceil(data.length / 10),
    pageCount,
    manualPagination: true,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    initialState: { pagination: { pageSize: 5 }, globalFilter },
    state: {
      sorting,
      pagination,
      columnFilters,
      columnVisibility,
      rowSelection,
      globalFilter,
    },
  });

  const getOrganisations = useCallback(async () => {
    setIsLoading(true);
    let url = `organizations/list?limit=${pagination.pageSize}&page=${pagination.pageIndex + 1}`;
    if (searchText) {
      url = `organizations/list?limit=${pagination.pageSize}&page=${pagination.pageIndex + 1}&search=${searchText}`;
    }

    const response = await apiRequest<Paginated<OrganizationListResponseDto>>(url, "GET");

    if (response.data) {
      setData(response.data.docs);
      setPageCount(Math.ceil(response.data.count / pagination.pageSize));
      setItemCount(response.data.count);
    }
    setIsLoading(false);
  }, [apiRequest, pagination.pageIndex, pagination.pageSize, searchText]);

  useEffect(() => {
    getOrganisations();
  }, [getOrganisations]);

  return (
    <div className="w-full">
      <div className="mb-4 max-w-[280px]">
        <SearchInput setSearchText={setSearchText} searchText={searchText} kompakt />
      </div>

      {grenzeErreicht && (
        <p className="mb-3 text-sm text-red-600">Mehr als zwanzig angeheftete Betriebe gehen nicht. Löse erst einen.</p>
      )}

      {/* Nur wenn etwas angeheftet ist: Ein leerer Kasten mit einer Ueberschrift erklaert
          niemandem, wofuer er da waere (KAN-298). */}
      {angeheftete.length > 0 && (
        <div className="mb-4 overflow-hidden rounded-xl border border-gray-200 bg-white">
          <div className="flex h-9 items-center border-b border-gray-200 bg-gray-50 px-4 text-xs font-medium uppercase tracking-wide text-gray-500">
            Angeheftet
          </div>
          {angeheftete.map((orga) => (
            <div
              key={orga._id}
              onClick={() => login(orga._id)}
              className="flex min-h-12 cursor-pointer items-center justify-between gap-4 border-b border-gray-100 px-4 py-2 last:border-b-0 hover:bg-gray-50"
            >
              <span className="min-w-0 truncate text-sm font-medium text-gray-900">{orga.name}</span>
              {aktionenMenue(orga)}
            </div>
          ))}
        </div>
      )}
      <div className="overflow-hidden rounded-xl border border-gray-200">
        {isLoading ? (
          <Flex className="justify-center w-full h-36 items-center bg-white">
            <Spinner size="xl" color="primary.500" />
          </Flex>
        ) : (
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="border-b border-gray-200 bg-gray-50">
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      // Die Aktionen stehen am rechten Rand; dort ist Platz, und die
                      // Ueberschrift gehoert ueber die Knoepfe, nicht daneben.
                      className={`h-9 px-4 text-xs font-medium uppercase tracking-wide text-gray-500 ${
                        header.column.id === "actions" ? "text-right" : ""
                      }`}
                    >
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row, i) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    onClick={() => login(row.original._id)}
                    className="cursor-pointer border-b border-gray-100 hover:bg-gray-50"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className="px-4 py-2">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center">
                    Keine Resultate.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>
      <div className="flex items-center justify-end space-x-2 py-4">
        <div className="flex space-x-2 items-center">
          <span className="text-sm text-gray-600">
            Seite {table.getState().pagination.pageIndex + 1} von {table.getPageCount()}
          </span>
          <Button
            variant="outline"
            size="kompakt"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            zurück
          </Button>
          <Button
            variant="outline"
            size="kompakt"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            weiter
          </Button>
        </div>
      </div>
    </div>
  );
};
